import type { DuplicateGroup, PhotoDecisions, PhotoRecord, QuarantineRecord, RootFolder, SavedSession } from "./types";

const DB_NAME = "phototidy-local";
const DB_VERSION = 2;
const SESSION_STORE = "sessions";
const QUARANTINE_STORE = "quarantine";
const REVIEW_STORE = "reviews";

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Local database request failed."));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Local database transaction failed."));
    transaction.onabort = () => reject(transaction.error ?? new Error("Local database transaction was cancelled."));
  });
}

async function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SESSION_STORE)) db.createObjectStore(SESSION_STORE, { keyPath: "id" });
      if (!db.objectStoreNames.contains(QUARANTINE_STORE)) db.createObjectStore(QUARANTINE_STORE, { keyPath: "id" });
      if (!db.objectStoreNames.contains(REVIEW_STORE)) db.createObjectStore(REVIEW_STORE, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Unable to open local PhotoTidy storage."));
  });
}

export async function saveSession(
  roots: RootFolder[],
  photos: PhotoRecord[],
  groups: DuplicateGroup[],
  decisions: PhotoDecisions,
  threshold: number,
): Promise<void> {
  const session: SavedSession = {
    id: "latest",
    savedAt: new Date().toISOString(),
    threshold,
    roots,
    photos: photos.map(({ file: _file, ...photo }) => photo),
    groups: groups.map(({ items, ...group }) => ({ ...group, itemIds: items.map((item) => item.id) })),
    decisions,
  };
  const db = await database();
  const transaction = db.transaction([SESSION_STORE, REVIEW_STORE], "readwrite");
  transaction.objectStore(SESSION_STORE).put(session);
  transaction.objectStore(REVIEW_STORE).delete("latest");
  await transactionDone(transaction);
  db.close();
}

export async function loadSession(): Promise<SavedSession | undefined> {
  const db = await database();
  const transaction = db.transaction([SESSION_STORE, REVIEW_STORE], "readonly");
  const sessionRequest = requestResult(transaction.objectStore(SESSION_STORE).get("latest")) as Promise<SavedSession | undefined>;
  const reviewRequest = requestResult(transaction.objectStore(REVIEW_STORE).get("latest")) as Promise<{ decisions: PhotoDecisions; originals: Record<string, string> } | undefined>;
  const [result, review] = await Promise.all([sessionRequest, reviewRequest]);
  await transactionDone(transaction);
  db.close();
  if (result && review) {
    result.decisions = review.decisions;
    for (const group of result.groups) group.manualOriginalId = review.originals[group.id];
  }
  return result;
}

export async function saveReviewState(decisions: PhotoDecisions, groups: Array<Pick<DuplicateGroup, "id" | "manualOriginalId">>): Promise<void> {
  const originals = Object.fromEntries(groups.filter((group) => group.manualOriginalId).map((group) => [group.id, group.manualOriginalId!]));
  const db = await database();
  const transaction = db.transaction(REVIEW_STORE, "readwrite");
  transaction.objectStore(REVIEW_STORE).put({ id: "latest", decisions: { ...decisions }, originals });
  await transactionDone(transaction);
  db.close();
}

export async function clearSession(): Promise<void> {
  const db = await database();
  const transaction = db.transaction([SESSION_STORE, REVIEW_STORE], "readwrite");
  transaction.objectStore(SESSION_STORE).delete("latest");
  transaction.objectStore(REVIEW_STORE).delete("latest");
  await transactionDone(transaction);
  db.close();
}

export async function saveQuarantineRecord(record: QuarantineRecord): Promise<void> {
  const db = await database();
  const transaction = db.transaction(QUARANTINE_STORE, "readwrite");
  transaction.objectStore(QUARANTINE_STORE).put(record);
  await transactionDone(transaction);
  db.close();
}

export async function listQuarantineRecords(): Promise<QuarantineRecord[]> {
  const db = await database();
  const transaction = db.transaction(QUARANTINE_STORE, "readonly");
  const records = await requestResult(transaction.objectStore(QUARANTINE_STORE).getAll()) as QuarantineRecord[];
  await transactionDone(transaction);
  db.close();
  return records.sort((left, right) => right.timestamp.localeCompare(left.timestamp));
}
